import {HttpContextToken, HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {catchError, EMPTY} from "rxjs";
import {inject, InjectionToken} from "@angular/core";
import {ToasterService} from "../services/toaster.service";
import {RouterService} from "../services/router.service";
import {LanguageService} from '../services/language.service';
import {ResetPasswordEnforcementService} from '../services/reset-password-enforcement.service';
import {TokenService} from '../services/token.service';
import * as Sentry from '@sentry/angular';
import {ApiProbeService} from '../services/api-probe.service';
import {EXPECT_EMPTY_NEWS_READERS_PAGE} from './news-readers-page-context';
import {isPasswordChangeRequiredHttpError} from '../services/validation-aware-error-handler';
import {LEGACY_TOKEN_MIGRATION_REQUEST} from './legacy-token-migration-context';

/**
 * Set to true on a request to bypass the maintenance interceptor
 * e.g. import { BYPASS_MAINT_INTERCEPTOR } from '../interceptors/status.interceptor';
 * return this.http.get<TYPE.Languages>(
    this.url + '/' + Config.API_VERSION_STRING + '/languages',
    {
      headers: this.headers,
      context: new HttpContext().set(BYPASS_MAINT_INTERCEPTOR, true)
    }
  );
 */
export const BYPASS_MAINT_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
export const REPORT_FEEDBACK_LOCK_FAILURE = new HttpContextToken<boolean>(() => false);
const INTERCEPTOR_TOAST_DEDUPE_TTL_MS = 5000;
const recentInterceptorToasts = new Map<string, number>();

//#region toasts
const cleanupExpiredInterceptorToastEntries = (now: number): void => {
  recentInterceptorToasts.forEach((timestamp, key) => {
    if (now - timestamp > INTERCEPTOR_TOAST_DEDUPE_TTL_MS) {
      recentInterceptorToasts.delete(key);
    }
  });
};

const shouldShowInterceptorToast = (title: string, message: string): boolean => {
  const now = Date.now();
  cleanupExpiredInterceptorToastEntries(now);

  const key = `${title}|${message}`;
  const previousTimestamp = recentInterceptorToasts.get(key);
  if (previousTimestamp !== undefined && now - previousTimestamp <= INTERCEPTOR_TOAST_DEDUPE_TTL_MS) {
    return false;
  }

  recentInterceptorToasts.set(key, now);
  return true;
};
//#endregion toasts
//#region sentry
const reportHttpErrorToSentry = (
  httpError: HttpErrorResponse,
  request: {
    method: string;
    url: string;
  },
  level: 'error' | 'warning',
  kind: 'client' | 'server',
  customerKey: string | null
): void => {
  Sentry.withScope(scope => {
    scope.setLevel(level);
    scope.setTag('http.error.kind', kind);
    scope.setTag('http.error.source', 'status-interceptor');
    scope.setTag('http.status_code', String(httpError.status));
    scope.setTag('http.method', request.method);

    if (customerKey) {
      scope.setTag('customer.key', customerKey);
    }

    scope.setContext('http.error', {
      method: request.method,
      requestUrl: request.url,
      responseUrl: httpError.url,
      status: httpError.status,
      statusText: httpError.statusText,
      message: httpError.message,
      reason: httpError.error?.reason,
      backendMessage: httpError.error?.message,
    });

    const message = `HTTP ${httpError.status}: ${request.method} ${request.url}`;

    Sentry.captureException(new Error(message));
  });
};

export const HTTP_ERROR_SENTRY_REPORTER = new InjectionToken<typeof reportHttpErrorToSentry>(
  'HTTP_ERROR_SENTRY_REPORTER',
  {providedIn: 'root', factory: () => reportHttpErrorToSentry}
);

const getCustomerKeyForSentry = (): string | null => {
  try {
    return localStorage.getItem('customerKey');
  } catch {
    return null;
  }
};
//#endregion sentry
//#region status 0
export const isCancelledStatusZeroRequest = (
  httpError: HttpErrorResponse
): boolean => {
  if (httpError.status !== 0) {
    return false;
  }

  const requestError = httpError.error;

  if (!requestError) {
    return false;
  }

  if (
    typeof ProgressEvent !== 'undefined' &&
    requestError instanceof ProgressEvent
  ) {
    return (
      requestError.type === 'abort' ||
      requestError.type === 'cancel'
    );
  }

  return (
    requestError.type === 'abort' ||
    requestError.type === 'cancel' ||
    requestError.name === 'AbortError'
  );
};
//#endregion status 0
//#region page unload
let isPageUnloading = false;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    isPageUnloading = true;
  });
}
//#endregion page unload

export const statusInterceptor: HttpInterceptorFn = (req, next) => {
  const toasterService = inject(ToasterService);
  const routerService = inject(RouterService);
  const lang = inject(LanguageService);
  const resetPasswordEnforcementService = inject(ResetPasswordEnforcementService);
  const tokenService = inject(TokenService);
  const apiProbeService = inject(ApiProbeService);
  const reportErrorToSentry = inject(HTTP_ERROR_SENTRY_REPORTER);
  const customerKey = getCustomerKeyForSentry();
  const isSentryRequest = req.url.includes('sentry.io') || req.url.includes('/api/embed/error-page/');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isSentryRequest || error.url?.includes('sentry.io')) {
        throw error;
      }

      // The migration endpoint authenticates with a legacy token, not the app session.
      // Let its caller handle rejected tokens without clearing the current app session.
      if ((error.status === 401 || error.status === 403) && req.context.get(LEGACY_TOKEN_MIGRATION_REQUEST)) {
        throw error;
      }

      const requestToken = req.headers.get('Authorization')?.match(/^Bearer (.+)$/)?.[1];
      // A response for a previous session must not affect the current login or session.
      if ((error.status === 401 || error.status === 403) && requestToken && requestToken !== tokenService.accessToken()) {
        return EMPTY;
      }

      if (error.status === 404 && req.context.get(EXPECT_EMPTY_NEWS_READERS_PAGE)) {
        throw error;
      }

      const isPasswordChangeRequired = isPasswordChangeRequiredHttpError(error);
      const isFeedbackLockFailure = req.context.get(REPORT_FEEDBACK_LOCK_FAILURE);
      const isExpectedClientError = error.status === 422 || error.status === 423;
      if (isFeedbackLockFailure && !isExpectedClientError && !isPasswordChangeRequired) {
        reportErrorToSentry(
          error,
          {method: req.method, url: req.url},
          'error',
          error.status >= 500 ? 'server' : 'client',
          customerKey
        );
      }

      const currentUrl = routerService.getCurrentUrl();
      const reason = error.error?.reason;
      if (error.status >= 500 && error.status < 600) {
        apiProbeService.probeApiHealthAfterServerError();
      }

      if (isPasswordChangeRequired) {
        const promoted = resetPasswordEnforcementService.promotePendingLoginContextToRequirement();
        const hasExistingRequirement = resetPasswordEnforcementService.hasPasswordChangeRequirement();

        if (!promoted && !hasExistingRequirement) {
          tokenService.clearToken();

          const title = lang.getTranslationByKey('USERISLOGGEDOUT');
          const message = lang.getTranslationByKey('USERREDIRECTTOLOGINSCREEN');
          if (shouldShowInterceptorToast(title, message)) {
            toasterService.error(title, message);
          }

          if (!currentUrl.startsWith('/auth')) {
            routerService.navigateTo('/auth');
          }

          throw error;
        }

        if (!currentUrl.startsWith('/auth/password/change')) {
          routerService.navigateTo('/auth/password/change');
        }
        throw error;
      }

      if(error.status === 503) {
        if (!routerService.getCurrentUrl().startsWith('/maintenance')) {
          routerService.navigateTo('/maintenance');
        }
      } else if(error.status === 401) {
        tokenService.clearToken();

        // Only redirect if not already on an auth page
        if (!routerService.getCurrentUrl().startsWith('/auth')) {
          const title = lang.getTranslationByKey('USERISLOGGEDOUT');
          const message = lang.getTranslationByKey('USERREDIRECTTOLOGINSCREEN');
          if (shouldShowInterceptorToast(title, message)) {
            toasterService.error(title, message);
          }
          routerService.navigateTo('/auth');
        }
      } else if(error.status === 403) {
        const title = lang.getTranslationByKey('USERINSUFFICIENTRIGHTS');
        const message = lang.getTranslationByKey('CONTACTADMINISTRATORFORACCESS');

        if (reason === 'GDPR_ACCEPT_REQUIRED') {
          if (!currentUrl.startsWith('/auth/login/notes')) {
            routerService.setPostLoginRedirect(currentUrl);
          }
          routerService.navigateTo('/auth/login/notes');
        } else if (shouldShowInterceptorToast(title, message)) {
          toasterService.warning(title, message);
        }
      } else if(error.status >= 400 && error.status < 500) {
        const title = lang.getTranslationByKey('ANERROROCCURED');
        const message = error?.error?.message ?? lang.getTranslationByKey('OCCUREDERRORISUNKNOWN');
        if (shouldShowInterceptorToast(title, message)) {
          toasterService.error(title, message);
        }
        const userTriedTooManyRequests = error.status === 429;
        const userEnteredWrongCustomerKey = error.status === 404 && req.url.includes('/customer') && req.url.includes('/public');

        if (!isFeedbackLockFailure && !isExpectedClientError && !userTriedTooManyRequests && !userEnteredWrongCustomerKey) {
          reportErrorToSentry(
            error,
            {
              method: req.method,
              url: req.url,
            },
            'warning',
            'client',
            customerKey
          );
        }
        if (!isExpectedClientError && !userTriedTooManyRequests) {
          console.error(error);
        }
      } else if ((error.status >= 500 && error.status < 600)) {
        const title = lang.getTranslationByKey('SERVERERROROCCURED');
        const message = lang.getTranslationByKey('TRYAGAINLATER') + ' ' + lang.getTranslationByKey('ERRORWILLAUTOMATICALLYBEREPORTED');
        if (shouldShowInterceptorToast(title, message)) {
          toasterService.error(title, message);
        }
        if (!isFeedbackLockFailure) {
          reportErrorToSentry(
            error,
            {
              method: req.method,
              url: req.url,
            },
            'error',
            'server',
            customerKey
          );
        }
        console.error(error);
      } else if(error.status === 0) {
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const isCancelled = isCancelledStatusZeroRequest(error);

        if (isOffline) {
          const title = lang.getTranslationByKey('NETWORKERRORONREQUEST');
          const message = lang.getTranslationByKey('DEVICEISOFFLINE');

          if (shouldShowInterceptorToast(title, message)) {
            toasterService.error(title, message);
          }

          console.warn('Network request failed because device is offline', {
            method: req.method,
            url: req.url,
            error
          });

        } else if (isPageUnloading || isCancelled) {
          // Ignore expected transport failures caused by navigation/unload/abort.
          console.debug('Ignored cancelled status-0 request', {
            method: req.method,
            url: req.url,
            isPageUnloading,
            isCancelled
          });
        } else {
          console.debug('Network request failed with status 0', {
            method: req.method,
            url: req.url,
            error
          });
        }
      }
      throw error;
    })
  );
};

